import {
    Component,
    OnInit,
    AfterViewInit,
    OnDestroy,
    Inject,
    ViewChild,
    Input
} from '@angular/core';
import { IUserGroups } from '../IUserGroups';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { IUserEntryDate } from '../IUserEntryDates';
import { Store, select } from '@ngrx/store';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info/store/user.reducer';
import * as fromUED_r from '../store/reducer';
import * as fromErrors from '../../shared';
import {
    LoadGroups,
    Reset,
    LoadUserEntryDates,
    UpdateUserEntryDates,
    LoadEntryDatesByGroup
} from '../store/actions';
import { takeWhile } from 'rxjs/operators';
import { UserentrydatesPresentersComponent } from '../presenters/userentrydates-presenters.component';
import { IUser, SetUserInfo } from '../../user-info';
import { DatePipe } from '@angular/common';
import * as fromShared from '../../shared';

@Component({
    selector: 'ef-userentrydates-features',
    templateUrl: './userentrydates-features.component.html',
    styleUrls: ['./userentrydates-features.component.scss'],
    standalone: false
})
export class UserentrydatesFeaturesComponent
    implements OnInit, AfterViewInit, OnDestroy {
    process$: Observable<fromShared.Process>;
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    public groups$: Observable<Array<IUserGroups>>;
    public groups: Array<IUserGroups> = [];
    public entryDates$: Observable<Array<IUserEntryDate>>;
    public entryDates: Array<IUserEntryDate> = [];

    errors$: Observable<string[]>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;

    public userSub: Subscription;
    @Input() public user: IUser;
    public startDate: string;
    public endDate: string;
    public entryStart: Date = new Date();
    public entryEnd: Date = new Date();

    @ViewChild(UserentrydatesPresentersComponent, { static: true })
    uEDC: UserentrydatesPresentersComponent;

    public active = true;

    constructor(
        private entryDatesStore: Store<fromUED_r.State>,
        @Inject(LoggingService) private log: LoggingService,
        private store: Store<fromUser.State>,
        private datePipe: DatePipe,
        private errorStore: Store<fromShared.Errors.State>
    ) {}

    ngOnDestroy() {
        this.active = false;
    }

    transformDate(date): Date {
        return new Date(this.datePipe.transform(date, 'yyyy-MM-dd'));
    }

    ngOnInit() {
        this.entryDatesStore.dispatch(new LoadGroups());
        this.entryDatesStore.dispatch(new LoadUserEntryDates());
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.groups$ = this.entryDatesStore.pipe(select(fromUED_r.getGroups));

        this.entryDates$ = this.entryDatesStore.pipe(
            select(fromUED_r.getEntryDates)
        );

        this.errors$ = this.store.pipe(select(fromUED_r.getEntryDatesErrors));

        this.selectedError$ = this.errorStore.pipe(
            select(fromShared.Errors.getShowErrors)
        );
    }

    ngAfterViewInit() {
        // getting the start and end date from the user info data in the store
        this.userSub = this.store
            .pipe(
                takeWhile(_ => this.active),
                select(fromUser.getUser)
            )
            .subscribe(user => {
                this.log.debug({
                    type: 'FixedPriceShellComponent:user-subscribe',
                    user
                });
                this.user = user;

                if (
                    user.userInfo.CurrentPayPeriodEnd &&
                    user.userInfo.CurrentPayPeriodStart
                ) {
                    this.startDate =
                        user.userInfo.CurrentPayPeriodStart.toString();
                    this.endDate = user.userInfo.CurrentPayPeriodEnd.toString();
                    this.entryStart = new Date(this.startDate);
                    this.entryEnd = new Date(this.endDate);
                    this.entryStart = this.transformDate(this.entryStart);
                    this.entryEnd = this.transformDate(this.entryEnd);
                }
            });
        this.groups$.pipe(takeWhile(_ => this.active)).subscribe(_groups => {
            this.groups = _groups;
        });

        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });

        this.entryDates$
            .pipe(takeWhile(_ => this.active))
            .subscribe(_entrydates => {
                this.entryDates = _entrydates;
            });

        this.uEDC.startDateSelected
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                this.log.debug({
                    type: 'GroupsFeatureComponent:startDateSelected',
                    value
                });
            });

        this.uEDC.endDateSelected
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                this.log.debug({
                    type: 'GroupsFeatureComponent:endDateSelected',
                    value
                });
            });

        this.uEDC.applyClicked
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.entryDatesStore.dispatch(
                        new UpdateUserEntryDates(value)
                    );
                }
            });

        this.uEDC.groupsSelected
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.entryDatesStore.dispatch(
                        new LoadEntryDatesByGroup(value)
                    );
                }
            });

        this.uEDC.resetAction
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                this.entryDatesStore.dispatch(new LoadUserEntryDates());
            });
    }

    public onResetData() {
        this.entryDatesStore.dispatch(new Reset());
    }

    onSaveEntryDates($event) {
        if ($event != null) {
            this.entryDatesStore.dispatch(
                new UpdateUserEntryDates([$event])
            );
            this.store.dispatch(
                new SetUserInfo({...this.user.userInfo, UserEntryStartDate: $event.MinWorkDate, UserEntryEndDate: $event.MaxWorkDate})
            );
        }
    }
    onMultiSaveEntryDates($event) {
        if ($event != null) {
            this.entryDatesStore.dispatch(
                new UpdateUserEntryDates($event)
            );
            
            const currentUserEntry = $event.find(x => x.EmployeeListId == this.user.userInfo.EmployeeListID) != null ? $event.find(x => x.EmployeeListId == this.user.userInfo.EmployeeListID) : '';
            if (currentUserEntry != '') {
                this.store.dispatch(
                    new SetUserInfo({...this.user.userInfo, UserEntryStartDate: currentUserEntry.MinWorkDate, UserEntryEndDate: currentUserEntry.MaxWorkDate})
                );
            }           
        }
    }
}
